/**
 * Fill in the Blanks Content Type — Player
 * Title + description, optional lesson (Page), then one or more FITB exercises
 */

import * as React from 'react';
import { cn } from '@ai-content-hub/content-kit';
import type { GradingResult, ElementChildLabels } from '@ai-content-hub/content-kit';
import { CompositePlayerGroupedItemsBody } from '@ai-content-hub/content-core';
import {
    FITBPlayer as FITBElementPlayer,
    type FITBAnswer,
    PagePlayer,
    RichTextInline,
} from '@ai-content-hub/content-elements';
import { isCorrectAnswerRevealed, useLocale } from '@ai-content-hub/content-core';
import type { FITBContentData, FITBContentConfig } from './schema';
import { normalizeFITBContentData } from './schema';
import type { FITBContentExercise } from './schema';
import type { FITBContentCompositeAnswer } from './compositeGrading';
import { normalizeFitbContentAnswer } from './compositeGrading';
import { FileText } from 'lucide-react';
import map from 'lodash/map';

export interface FITBContentPlayerProps {
    data?: FITBContentData;
    value?: FITBContentData;
    config?: FITBContentConfig;
    /** Per-exercise map by exercise id, or legacy single-object blank answers */
    answer?: FITBAnswer | FITBContentCompositeAnswer;
    onAnswer?: (answer: FITBContentCompositeAnswer) => void;
    isReview?: boolean;
    disabled?: boolean;
    showFeedback?: boolean;
    result?: GradingResult;
    className?: string;
}

export const FITBContentPlayer: React.FC<FITBContentPlayerProps> = ({
    data: dataProp,
    value: valueProp,
    config,
    answer,
    onAnswer,
    isReview = false,
    disabled = false,
    showFeedback = false,
    result,
    className,
}) => {
    const data = dataProp ?? valueProp;

    const safeData = normalizeFITBContentData(data);
    const { t } = useLocale({ config: config?.i18n });
    // ELMS-2370: LMS "hide answer" => behavior.showCorrectAnswer === false.
    const revealCorrect = isCorrectAnswerRevealed(config);

    const hasDescription = Boolean(safeData.description?.trim());
    const hasLessonContent = (safeData.lessonContent?.sections?.length ?? 0) > 0;

    const exerciseIds = React.useMemo(() => map(safeData.exercises, (e) => e.id), [safeData.exercises]);
    const answersByEx = normalizeFitbContentAnswer(answer, exerciseIds);

    const exerciseById = React.useMemo(
        () => new Map(map(safeData.exercises, (e) => [e.id, e] as const)),
        [safeData.exercises]
    );

    // Build elementChildLabels per exercise: blanks are numbered sequentially
    // across all exercises (e.g. exercise 1 blanks → 1,2; exercise 2 blanks → 3,4).
    const totalBlanks = React.useMemo(
        () => safeData.exercises.reduce((sum, ex) => sum + (ex.data.blanks?.length ?? 0), 0),
        [safeData.exercises],
    );
    const showBlankLabels = totalBlanks > 1;
    const childLabelsPerExercise = React.useMemo(() => {
        if (!showBlankLabels) return new Map<string, ElementChildLabels>();
        const labelsMap = new Map<string, ElementChildLabels>();
        let runningN = 1;
        for (const ex of safeData.exercises) {
            const blanks = ex.data.blanks ?? [];
            const fitbBlank: Record<string, number> = {};
            for (let i = 0; i < blanks.length; i++) {
                fitbBlank[`blank-${i}`] = runningN++;
            }
            labelsMap.set(ex.id, { fitbBlank });
        }
        return labelsMap;
    }, [safeData.exercises, showBlankLabels]);

    const handleSubAnswer = (exId: string, next: FITBAnswer) => {
        onAnswer?.({ ...answersByEx, [exId]: next });
    };

    const renderExercise = (ex: FITBContentExercise) => (
        <div
            key={ex.id}
            className="fitb-content-player-exercise border-border border-b border-dashed pb-8 last:border-b-0 last:pb-0"
        >
            <FITBElementPlayer
                data={ex.data}
                answer={answersByEx[ex.id] ?? {}}
                onAnswer={(next) => handleSubAnswer(ex.id, next)}
                isReview={isReview}
                disabled={disabled}
                showFeedback={showFeedback}
                result={safeData.exercises.length === 1 ? result : undefined}
                config={config as Record<string, unknown>}
                elementChildLabels={childLabelsPerExercise.get(ex.id)}
            />
        </div>
    );

    const body =
        exerciseIds.length === 0 ? null : (
            <CompositePlayerGroupedItemsBody
                prefix="fitb-content-player"
                itemIds={exerciseIds}
                questionsSidebarRows={safeData.questionsSidebarRows}
                renderItem={(id) => {
                    const ex = exerciseById.get(id);
                    return ex ? renderExercise(ex) : null;
                }}
            />
        );

    return (
        <div className={cn('fitb-content-player flex flex-col gap-4', className)}>
            {hasDescription && (
                <div className="fitb-content-player-header">
                    <RichTextInline
                        text={safeData.description ?? ''}
                        as="p"
                        className="fitb-content-player-description text-sm text-muted-foreground leading-relaxed"
                    />
                </div>
            )}

            {hasLessonContent && (
                <div className="fitb-content-player-lesson">
                    <div className="fitb-content-player-lesson-header flex items-center gap-2 pb-3 border-b border-border mb-3">
                        <FileText className="h-5 w-5 text-primary shrink-0" aria-hidden />
                        <h3 className="font-semibold text-foreground">{t('contentTab', 'Content')}</h3>
                    </div>
                    <PagePlayer data={safeData.lessonContent} submitted={showFeedback && revealCorrect} />
                </div>
            )}

            <div className="fitb-content-player-body flex flex-col gap-8">{body}</div>
        </div>
    );
};

export default FITBContentPlayer;
